/* Shop by Chemical — drumpump.com
 * Search a chemical, see every pump its manufacturer rates for it, narrow with filters.
 * Needs pump-engine.js, loaded first. Data: products from the fmp-json product feed (metafields only), ratings from
 * pump-chemicals.json, wording and filters from sbc-rules.json. Every safety decision (rated, check with us, flammable,
 * bundled hose, product fields) is made by PumpEngine; this file only lays the results out. It contains no brand names:
 * chart names and documents come from pump-chemicals.json.
 */
(function () {
  'use strict';
  var inNode = typeof module !== 'undefined' && module.exports;
  var PE = inNode ? require('./pump-engine.js') : window.PumpEngine;

  // ------------------------------------------------------------------ view model (pure functions, run in node for tests)
  var engine = {};
  function arr(v) { return Array.isArray(v) ? v : []; }
  function uniq(list) { return list.filter(function (v, i) { return list.indexOf(v) === i; }); }

  // Groundable plus every certification the manufacturer states, in its own wording
  engine.compliance = function (p) { return { grounding: PE.grounding(p), groundable: PE.grounding(p) === 'included', certs: uniq(arr(p.certifications).filter(Boolean)) }; };
  engine.item = function (p, extra) {
    var it = Object.assign({ rating: null, rank: 0, sources: [], reasons: [] }, extra || {}, { p: p, type: p.pump_type, compliance: engine.compliance(p), hose: PE.bundledHose(p) });
    // A required upgrade replaces parts the liquid touches, and certifications describe the pump as sold: not shown or filtered on
    if (arr(it.parts).length) it.compliance = { grounding: it.compliance.grounding, groundable: it.compliance.groundable, certs: [] };
    it.total = Number(p.price || 0) + arr(it.parts).reduce(function (t, part) { return t + Number(part.product.price || 0); }, 0);  // what the customer pays
    return it;
  };
  // listed: also return pumps named for the liquid on their manufacturer's product page (the section setting)
  engine.evaluate = function (products, chems, data, answers, listed) {
    var ev = PE.evaluate(products, chems, data, answers, { listed: !!listed }), wrap = function (list) { return list.map(function (x) { return engine.item(x.p, x); }); };
    return { results: wrap(ev.results), upgrades: wrap(ev.upgrades), askUs: wrap(ev.askUs), listed: wrap(ev.listed) };
  };
  // What the product pages name for the listed items, as a readable list ("motor oil and transmission fluid (ATF)")
  engine.namedText = function (items) {
    var seen = [];
    arr(items).forEach(function (it) { arr(it.listed).forEach(function (l) { if (seen.indexOf(l.label) < 0) seen.push(l.label); }); });
    return seen.length < 2 ? seen.join('') : seen.slice(0, -1).join(', ') + ' and ' + seen[seen.length - 1];
  };
  engine.fill = function (text, values) { return String(text || '').replace(/\{(\w+)\}/g, function (m, k) { return values[k] != null ? values[k] : m; }); };
  // Unique "Label: text" lines for warnings or reasons, with the chemical named when several are selected
  engine.noteLines = function (list, data, several) {
    return uniq(arr(list).map(function (r) { return (several ? r.chem.n + ' · ' : '') + PE.sourceLabels(data, [r.source])[0] + ': ' + r.text; }));
  };

  // Browse mode: every pump in the tools with no chemical selected. No rating is attached, so the UI must not show one.
  // Types are interleaved (one of each in turn, cheapest first) so the first screen is a mix rather than 24 of one kind.
  engine.browse = function (products, data, rules) {
    var groups = {}, found = [];
    products.forEach(function (p) {
      if (!PE.usable(p, data)) return;
      var it = engine.item(p);
      if (!groups[it.type]) { groups[it.type] = []; found.push(it.type); }
      groups[it.type].push(it);
    });
    var order = arr(rules.pump_types && rules.pump_types.order);
    var lists = order.filter(function (t) { return groups[t]; }).concat(found.filter(function (t) { return order.indexOf(t) < 0; }).sort()).map(function (t) {
      return groups[t].sort(function (a, b) { return (a.p.price - b.p.price) || (a.p.title || '').localeCompare(b.p.title || ''); });
    });
    var out = [];
    for (var i = 0; lists.some(function (l) { return i < l.length; }); i++) lists.forEach(function (l) { if (i < l.length) out.push(l[i]); });
    return out;
  };

  engine.humanize = function (v) { var s = String(v).replace(/[-_]+/g, ' ').trim(); return s.charAt(0).toUpperCase() + s.slice(1); };
  // Filter options from the rules, then every value in the catalog that no option covers, so new products always show up.
  // Returns { container: [[label, values]], power: [[label, values]], compliance: ['grounded', certification...], type: [...] }
  engine.options = function (items, rules) {
    function group(name, field) {
      var opts = rules.filters[name].options.map(function (o) { return [o[0], arr(o[1])]; }), covered = {}, extra = [];
      opts.forEach(function (o) { o[1].forEach(function (v) { covered[v] = true; }); });
      items.forEach(function (it) { [].concat(it.p[field] || []).forEach(function (v) { if (v && !covered[v]) { covered[v] = true; extra.push(v); } }); });
      var labels = rules.filters[name].labels || {}, known = Object.keys(labels);
      var rank = function (v) { return known.indexOf(v) >= 0 ? known.indexOf(v) : known.length; };
      extra.sort(function (a, b) { return rank(a) - rank(b) || (a < b ? -1 : a > b ? 1 : 0); });  // labelled values in the rules' order, then A to Z
      return opts.concat(extra.map(function (v) { return [labels[v] || engine.humanize(v), [v]]; }));
    }
    var certs = [], types = [];
    items.forEach(function (it) {
      it.compliance.certs.forEach(function (c) { if (certs.indexOf(c) < 0) certs.push(c); });
      if (types.indexOf(it.type) < 0) types.push(it.type);
    });
    return { container: group('container', 'containers'), power: group('power', 'power'), compliance: ['grounded'].concat(certs.sort()), type: types };
  };

  engine.applyFilters = function (items, f, opts) {
    function values(name, label) { var o = opts[name].filter(function (x) { return x[0] === label; })[0]; return o ? o[1] : []; }
    return items.filter(function (it) {
      if (f.type.length && f.type.indexOf(it.type) < 0) return false;
      if (f.container.length && !f.container.some(function (l) { return values('container', l).some(function (tag) { return arr(it.p.containers).indexOf(tag) >= 0; }); })) return false;
      if (f.power.length && !f.power.some(function (l) { return values('power', l).indexOf(it.p.power) >= 0; })) return false;
      for (var i = 0; i < f.compliance.length; i++) {
        var c = f.compliance[i];
        if (c === 'grounded' ? it.compliance.grounding !== 'included' : it.compliance.certs.indexOf(c) < 0) return false;
      }
      if (f.priceMax != null && it.total > f.priceMax) return false;
      return true;
    });
  };

  // Counts shown beside each option = results you would get by ticking it. Options inside a group are OR'd, so a group's
  // counts ignore that group's own selection; compliance options are AND'd, so they count against the full selection.
  // Pass no filters to count the items as given.
  engine.facets = function (items, opts, f) {
    var out = { type: {}, container: {}, power: {}, compliance: {}, priceMin: Infinity, priceMax: 0 };
    function without(key) {
      if (!f) return items;
      var g = { type: f.type, container: f.container, power: f.power, compliance: f.compliance, priceMax: f.priceMax };
      if (key) g[key] = key === 'priceMax' ? null : [];
      return engine.applyFilters(items, g, opts);
    }
    function inc(o, k) { o[k] = (o[k] || 0) + 1; }
    without('type').forEach(function (it) { inc(out.type, it.type); });
    without('container').forEach(function (it) { opts.container.forEach(function (o) { if (o[1].some(function (t) { return arr(it.p.containers).indexOf(t) >= 0; })) inc(out.container, o[0]); }); });
    without('power').forEach(function (it) { opts.power.forEach(function (o) { if (o[1].indexOf(it.p.power) >= 0) inc(out.power, o[0]); }); });
    without(null).forEach(function (it) { if (it.compliance.grounding === 'included') inc(out.compliance, 'grounded'); it.compliance.certs.forEach(function (c) { inc(out.compliance, c); }); });
    without('priceMax').forEach(function (it) { out.priceMin = Math.min(out.priceMin, it.total); out.priceMax = Math.max(out.priceMax, it.total); });
    if (out.priceMin === Infinity) out.priceMin = 0;
    return out;
  };

  // "Check with us" pumps grouped by the reasons given, so a condition shown once covers every pump it applies to
  engine.askGroups = function (askUs, data, several) {
    var groups = [], byKey = {};
    askUs.forEach(function (it) {
      var lines = uniq(it.reasons.map(function (r) {
        return (several ? r.chem.n + ' · ' : '') + PE.sourceLabels(data, [r.source])[0] + ': ' + r.text;
      }));
      var key = lines.join('\n');
      if (!byKey[key]) { byKey[key] = { lines: lines, texts: uniq(it.reasons.map(function (r) { return r.text; })), items: [] }; groups.push(byKey[key]); }
      byKey[key].items.push(it);
    });
    return groups;
  };
  // Links to the parts a group's conditions name. items and data (optional): a link with a part id is left out when that part
  // fits none of the group's pumps (a standard-only part and a group of groundable pumps).
  engine.partLinks = function (texts, rules, items, data) {
    return arr(rules.parts && rules.parts.links).filter(function (l) {
      var rx; try { rx = new RegExp(l.match, 'i'); } catch (e) { return false; }
      if (!texts.some(function (t) { return rx.test(t); })) return false;
      return !(l.part && items && data) || items.some(function (it) { return PE.partFits(it.p, data, l.part); });
    });
  };

  // ------------------------------------------------------------------ UI
  function el(tag, attrs, children) {
    var e = document.createElement(tag);
    Object.keys(attrs || {}).forEach(function (k) {
      if (k === 'class') e.className = attrs[k];
      else if (k.indexOf('on') === 0) e.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null) e.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c != null && c !== false) e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return e;
  }
  function money(v) { return '$' + Number(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','); }
  function listText(words) { return words.length < 2 ? words.join('') : words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]; }

  // Inline icons: 24x24, stroke = currentColor. Static markup, no user data.
  var ICONS = {
    search: '<circle cx="11" cy="11" r="7"/><path d="M20 20l-4.2-4.2"/>',
    flame: '<path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-2.14-.22-4.05 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.15.43-2.29 1-3a2.5 2.5 0 0 0 2.5 2.5z" fill="currentColor" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>',
    ground: '<path d="M12 3v8"/><path d="M4 11h16"/><path d="M7 15h10"/><path d="M10 19h4"/>',
    food: '<path d="M4 3v6a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3"/><path d="M6 11v10"/><path d="M20 21V3a4 4 0 0 0-4 4v6h4"/>',
    check: '<path d="M20 6L9 17l-5-5"/>',
    alert: '<path d="M12 3l9.5 17h-19z"/><path d="M12 10v4"/><path d="M12 17.5v.01"/>',
    droplet: '<path d="M12 3.2l5 5.3a7 7 0 1 1-10 0z"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6"/><path d="M12 7.5v.01"/>',
    tool: '<path d="M14.5 5.5a4 4 0 0 0 5 5L21 9a6 6 0 0 1-8.5 8.5L5 21 3 19l3.5-7.5A6 6 0 0 1 15 3z"/>',
    drum: '<ellipse cx="12" cy="5.5" rx="7" ry="2.8"/><path d="M5 5.5v13c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8v-13"/><path d="M5 12c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8"/>',
    bolt: '<path d="M13 2L4.5 13H11l-1 9 8.5-11H12z"/>',
    shield: '<path d="M12 21.5s7.5-3.7 7.5-9.5V5.2L12 2.5 4.5 5.2V12c0 5.8 7.5 9.5 7.5 9.5z"/>',
    tag: '<path d="M20.5 12.5l-8 8-9-9V3.5h8z"/><path d="M7.5 7.5v.01"/>',
    help: '<circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.6 2.6 0 0 1 5 .9c0 1.7-2.5 2.1-2.5 3.6"/><path d="M12 17.5v.01"/>',
    compass: '<circle cx="12" cy="12" r="9"/><path d="M15.5 8.5l-2 5-5 2 2-5z"/>',
    arrow: '<path d="M5 12h14"/><path d="M13 6l6 6-6 6"/>',
    plus: '<path d="M12 5v14"/><path d="M5 12h14"/>',
    arrowup: '<path d="M12 19V5"/><path d="M6 11l6-6 6 6"/>',
    nomatch: '<circle cx="11" cy="11" r="7"/><path d="M20 20l-4.2-4.2"/><path d="M8.6 8.6l4.8 4.8M13.4 8.6l-4.8 4.8"/>'
  };
  function icon(name, cls) {
    if (!ICONS[name]) return null;
    var s = document.createElement('span');
    s.className = 'sbc-icon' + (cls ? ' ' + cls : ''); s.setAttribute('aria-hidden', 'true');
    s.innerHTML = '<svg viewBox="0 0 24 24" focusable="false">' + ICONS[name] + '</svg>';
    return s;
  }
  function flag(label, kind) { return el('span', { class: 'sbc-flag' + (kind ? ' is-' + kind : '') }, [icon('flame'), el('span', {}, [label])]); }
  // Shopify.routes.root is "/" or a locale/market prefix such as "/en-ca/"; storefront URLs must be built from it.
  function shopRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  // ?src=… on the page URL (the homepage hero adds src=home) goes out with every event, so entry points can be compared
  var SRC = (function () { try { return new URLSearchParams(window.location.search).get('src') || ''; } catch (e) { return ''; } })();
  function track(ev, payload) { try { if (window.dataLayer) window.dataLayer.push(Object.assign({ event: ev }, SRC ? { sbc_src: SRC } : {}, payload || {})); } catch (e) {} }

  function fetchProducts(root, handle) {
    var direct = root.getAttribute('data-products-url');
    if (direct) return fetch(direct).then(function (r) { return r.json(); }).then(function (j) { return j.products || j; });
    function page(n) {
      return fetch(shopRoot() + 'collections/' + encodeURIComponent(handle) + '?view=fmp-json&page=' + n, { credentials: 'same-origin' }).then(function (r) { return r.text(); }).then(function (t) { return JSON.parse(t); });
    }
    return page(1).then(function (first) {
      var rest = []; for (var n = 2; n <= (first.pages || 1); n++) rest.push(page(n));
      return Promise.all(rest).then(function (others) { return others.reduce(function (acc, o) { return acc.concat(o.products || []); }, first.products || []); });
    });
  }

  var mountCount = 0;
  // The homepage hero sends typed text as ?q=…: it only prefills the search box, never a result
  engine.queryFromUrl = function (search) { return (new URLSearchParams(search || '').get('q') || '').trim().slice(0, 80); };

  function emptyFilters() { return { type: [], container: [], power: [], compliance: [], priceMax: null }; }

  function mount(root) {
    if (root.__sbc) return root.__sbc;
    var handle = root.getAttribute('data-collection') || 'find-my-pump';  // rules.collection_handle is the documented default; the section setting wins
    var contactUrl = root.getAttribute('data-contact') || '/pages/contact';
    var allowBrowse = root.getAttribute('data-browse') !== 'false';
    var fmpUrl = root.getAttribute('data-fmp-url');  // absent when the section's "Link to Find My Pump" is off
    var showListed = root.getAttribute('data-listed') === 'true';  // section setting: pumps designed for the liquid per the manufacturer's product page
    // ids are prefixed per instance so two sections on one page (or a theme-editor re-render) never share ids
    var uid = 'sbc-' + (root.getAttribute('data-section-id') || ++mountCount);
    var cartUrl = root.getAttribute('data-cart-url');  // where to go after adding a pump and its upgrade; defaults to the cart
    var listeners = typeof AbortController !== 'undefined' ? new AbortController() : null;
    function on(target, type, fn, opts) { var o = Object.assign({}, opts || {}); if (listeners) o.signal = listeners.signal; target.addEventListener(type, fn, o); }
    var rules, data, chems, products, browseItems, opts;
    var state = { chems: [], flam: {}, filters: emptyFilters(), browse: false, shown: 0, shownListed: 0, drawer: false };
    var refs = {};
    root.__sbc = { destroy: function () { if (listeners) listeners.abort(); document.body.classList.remove('sbc-lock'); root.__sbc = null; } };

    // ---- skeleton
    root.innerHTML = '';
    root.appendChild(el('div', { class: 'sbc-shell' }, [
      el('div', { class: 'sbc-hero' }, [el('div', { class: 'sbc-skel sbc-skel-search' })]),
      el('div', { class: 'sbc-sheet' }, [el('div', { class: 'sbc-skel sbc-skel-title' }), el('div', { class: 'sbc-skel sbc-skel-row' })])
    ]));

    Promise.all([
      PE ? Promise.resolve() : Promise.reject(new Error('pump-engine.js is not loaded')),
      fetch(root.getAttribute('data-rules')).then(function (r) { return r.json(); }),
      fetch(root.getAttribute('data-chemicals')).then(function (r) { return r.json(); }),
      fetchProducts(root, handle)
    ]).then(function (res) {
      rules = res[1]; data = res[2]; chems = PE.chemicals(data); products = res[3];
      if (!root.getAttribute('data-contact') && rules.contact_url) contactUrl = rules.contact_url;
      browseItems = engine.browse(products, data, rules);
      opts = engine.options(browseItems, rules);
      readUrl(); build(); watchStickyHeader(); render();
      if (state.query) prefill(state.query);
    }).catch(function (err) {
      root.innerHTML = '';
      root.appendChild(el('p', { class: 'sbc-error' }, ['The search didn’t load. ', el('a', { href: contactUrl }, ['Contact us']), ' and we’ll help you pick a pump.']));
      if (window.console) console.error('Shop by Chemical failed to load', err);
    });

    // ---- URL state
    function readUrl() {
      var q = new URLSearchParams(location.search);
      (q.get('chemical') || '').split(',').filter(Boolean).forEach(function (id) { if (PE.byId(chems, id) && state.chems.indexOf(id) < 0 && state.chems.length < rules.max_chemicals) state.chems.push(id); });
      var valid = { type: opts.type, container: opts.container.map(function (o) { return o[0]; }), power: opts.power.map(function (o) { return o[0]; }), compliance: opts.compliance };
      ['type', 'container', 'power', 'compliance'].forEach(function (k) { var v = q.get(k); if (v) state.filters[k] = v.split('|').filter(function (x) { return valid[k].indexOf(x) >= 0; }); });
      var mx = Number(q.get('max')); if (q.get('max') && isFinite(mx) && mx > 0) state.filters.priceMax = mx;
      (q.get('flam') || '').split(',').forEach(function (kv) { var p = kv.split(':'); if (p[0] && p[1]) state.flam[p[0]] = p[1]; });
      state.browse = allowBrowse && q.get('browse') === '1';
      state.query = engine.queryFromUrl(location.search);
    }
    function mode() { return state.chems.length ? 'chemical' : state.browse ? 'browse' : 'idle'; }
    function writeUrl(push) {
      var q = new URLSearchParams(location.search);
      ['chemical', 'browse', 'type', 'container', 'power', 'compliance', 'max', 'flam', 'q'].forEach(function (k) { q.delete(k); });
      var m = mode();
      if (state.chems.length) q.set('chemical', state.chems.join(','));
      if (m === 'browse') q.set('browse', '1');
      if (m !== 'idle') {
        ['type', 'container', 'power', 'compliance'].forEach(function (k) { if (state.filters[k].length) q.set(k, state.filters[k].join('|')); });
        if (state.filters.priceMax != null) q.set('max', String(state.filters.priceMax));
      }
      Object.keys(state.flam).forEach(function (id) { if (state.chems.indexOf(id) < 0) delete state.flam[id]; });
      var fl = Object.keys(state.flam).map(function (k) { return k + ':' + state.flam[k]; }); if (fl.length) q.set('flam', fl.join(','));
      var s = q.toString(), url = location.pathname + (s ? '?' + s : '') + location.hash;
      if (push) history.pushState(null, '', url); else history.replaceState(null, '', url);
      state.lastSearch = location.search;
    }

    // ---- static frame
    function build() {
      root.innerHTML = '';
      var labels = PE.sourceLabels(data, Object.keys(data.sources || {}));
      var hero = el('section', { class: 'sbc-hero' }, [
        root.hasAttribute('data-heading') ? el('h1', { class: 'sbc-title' }, [root.getAttribute('data-heading')]) : null,
        root.hasAttribute('data-intro') ? el('p', { class: 'sbc-lede' }, [root.getAttribute('data-intro')]) : null,
        buildSearch(),
        el('div', { class: 'sbc-trust' }, [el('span', {}, [chems.length.toLocaleString() + ' chemicals']), labels.length ? el('span', {}, ['Ratings from ' + listText(labels)]) : null, el('span', {}, ['Common names work too (MEK, DEF)'])]),
        refs.chips = el('div', { class: 'sbc-chips' })
      ]);
      refs.side = el('aside', { class: 'sbc-side', id: uid + '-side', 'aria-label': 'Filters' });
      refs.main = el('div', { class: 'sbc-main' });
      refs.toolbar = el('div', { class: 'sbc-toolbar' });
      var grid = el('div', { class: 'sbc-grid' }, [refs.side, el('div', {}, [refs.toolbar, refs.main])]);
      var sheet = refs.sheet = el('div', { class: 'sbc-sheet' }, [grid]);
      var legal = root.parentNode && Array.prototype.filter.call(root.parentNode.children, function (n) { return n.hasAttribute('data-sbc-legal'); })[0];
      if (legal) { legal.hidden = false; sheet.appendChild(legal); }
      refs.backdrop = el('div', { class: 'sbc-backdrop', onclick: function () { toggleDrawer(false); } });
      refs.status = el('p', { class: 'sbc-visually-hidden', 'aria-live': 'polite', role: 'status' });
      root.appendChild(refs.status);
      refs.shell = el('div', { class: 'sbc-shell' }, [hero, sheet, refs.backdrop]);
      root.appendChild(refs.shell);
    }

    function isFull() { return state.chems.length >= rules.max_chemicals; }
    function addChemical(c) {
      if (state.chems.indexOf(c.id) < 0) {
        if (isFull()) { announce('You can add up to ' + rules.max_chemicals + ' liquids. Remove one to add another.'); return; }
        state.chems.push(c.id);
        track('sbc_search', { sbc_chemical: c.n, sbc_position: state.chems.length });
      }
      var fromChips = refs.chips.contains(document.activeElement);
      state.adding = false; state.addQuery = ''; state.shown = 0; state.shownListed = 0;
      render();
      // keep keyboard focus nearby: the add tile, or the newest chemical once the limit is reached (never a Remove button)
      if (fromChips) { var chips = refs.chips.querySelectorAll('.sbc-chem'), next = refs.chips.querySelector('.sbc-chem-add') || chips[chips.length - 1]; if (next) next.focus(); }
    }

    // Accessible combobox over the chemical list. Focus or a click opens every chemical A–Z; typing narrows the list
    // (PumpEngine.browse). Rows are added a page at a time as the list scrolls. o: { suffix, placeholder, label, onEscape, onBlur }
    function combobox(o) {
      var listId = uid + '-' + o.suffix, PAGE = 60;
      var input = el('input', { class: 'form-field-input sbc-search', type: 'search', placeholder: o.placeholder, autocomplete: 'off', role: 'combobox', 'aria-expanded': 'false', 'aria-controls': listId, 'aria-autocomplete': 'list', 'aria-label': o.label });
      var list = el('ul', { class: 'sbc-suggest', id: listId, role: 'listbox', hidden: '' });
      var active = -1, items = [], rows = [];
      function close() { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; }
      function open() { list.hidden = !list.children.length; input.setAttribute('aria-expanded', String(!!items.length)); }
      function row(c, i) {
        var meta = [c.cas ? 'CAS ' + c.cas : null, PE.sourceLabels(data, c.src).join(' + ')].filter(Boolean).join(' · '), fire = PE.flameLabel(c);
        return el('li', { role: 'option', id: listId + '-' + i, 'aria-selected': 'false', 'aria-posinset': String(i + 1), 'aria-setsize': String(items.length), class: 'sbc-suggest-item', onmousedown: function (e) { e.preventDefault(); choose(c); } }, [
          el('span', { class: 'sbc-suggest-name' }, [c.n]), el('span', { class: 'sbc-suggest-meta' }, [meta]), fire ? flag(fire) : null
        ]);
      }
      function more() { for (var i = rows.length, end = Math.min(items.length, rows.length + PAGE); i < end; i++) rows.push(list.appendChild(row(items[i], i))); }
      function paint() {
        list.innerHTML = ''; rows = []; active = -1; input.removeAttribute('aria-activedescendant');
        var q = input.value.trim();
        if (isFull() && q.length >= 2) list.appendChild(el('li', { class: 'sbc-suggest-full', role: 'presentation' }, ['You can add up to ' + rules.max_chemicals + ' liquids. Remove one to add another.']));
        if (!isFull() && !items.length && q.length >= 3) {
          list.appendChild(el('li', { class: 'sbc-suggest-none', role: 'presentation' }, [
            el('span', {}, ['No manufacturer rating for “' + q + '”. ']),
            el('a', { href: contactFor(q), onmousedown: function (e) { e.preventDefault(); }, onclick: function () { track('sbc_search_no_match', { sbc_query: q }); } }, ['Contact us'])
          ]));
        }
        if (items.length && !q) list.appendChild(el('li', { class: 'sbc-suggest-head', role: 'presentation' }, [PE.browseNote(items.length)]));
        more();
        list.scrollTop = 0;
        open();
      }
      function refresh() { items = isFull() ? [] : PE.browse(chems, input.value); paint(); }
      // moves the highlight, adding rows until it exists, and scrolls the list (not the page) to it
      function setActive(i) {
        if (rows[active]) { rows[active].classList.remove('is-active'); rows[active].setAttribute('aria-selected', 'false'); }
        active = i;
        while (active >= rows.length && rows.length < items.length) more();
        var li = rows[active];
        if (!li) return;
        li.classList.add('is-active'); li.setAttribute('aria-selected', 'true'); input.setAttribute('aria-activedescendant', li.id);
        if (li.offsetTop < list.scrollTop) list.scrollTop = li.offsetTop;
        else if (li.offsetTop + li.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = li.offsetTop + li.offsetHeight - list.clientHeight;
      }
      list.addEventListener('scroll', function () { if (rows.length < items.length && list.scrollTop + list.clientHeight > list.scrollHeight - 200) more(); });
      list.addEventListener('mousedown', function (e) { if (!e.target.closest('a')) e.preventDefault(); });  // dragging the scrollbar keeps the field focused and the list open
      function choose(c) { input.value = ''; items = []; rows = []; list.innerHTML = ''; close(); addChemical(c); }
      input.addEventListener('input', refresh);
      input.addEventListener('focus', function () { if (list.hidden) refresh(); });
      input.addEventListener('click', function () { if (list.hidden) refresh(); });
      // Focus moving from the input into the list (Tab to the "Contact us" link) keeps the list open
      function leave(e) {
        var to = e.relatedTarget;
        if (to && (to === input || list.contains(to))) return;
        setTimeout(function () { close(); if (o.onBlur) o.onBlur(input); }, 120);
      }
      input.addEventListener('blur', leave);
      list.addEventListener('focusout', leave);
      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (list.hidden) refresh(); if (items.length) setActive(Math.min(active + 1, items.length - 1)); }
        else if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); setActive(Math.max(active - 1, 0)); }
        // Enter picks the highlighted row, or the best match for typed text; with nothing typed it never picks the first name
        else if (e.key === 'Enter') { e.preventDefault(); if (active >= 0 && items[active]) choose(items[active]); else if (input.value.trim() && items.length) choose(items[0]); }
        else if (e.key === 'Escape') { if (!list.hidden) { items = []; rows = []; input.value = ''; list.innerHTML = ''; close(); } else if (o.onEscape) o.onEscape(); }
      });
      return { input: input, list: list };
    }

    function buildSearch() {
      var cb = combobox({ suffix: 'suggest', placeholder: 'Chemical or CAS number', label: 'Search chemicals' });
      refs.input = cb.input;
      return el('div', { class: 'sbc-searchwrap' }, [icon('search', 'sbc-searchicon'), cb.input, cb.list]);
    }
    function syncSearchPlaceholder() {
      var n = state.chems.length;
      refs.input.placeholder = isFull() ? 'Remove a liquid to add another' : n ? 'Add another liquid' : 'Chemical or CAS number';
    }

    function announce(text) { if (refs.status) refs.status.textContent = text; }
    // ---- rendering
    function selectedChems() { return state.chems.map(function (id) { return PE.byId(chems, id); }).filter(Boolean); }

    function render() {
      var focusId = document.activeElement && refs.side && refs.side.contains(document.activeElement) ? document.activeElement.id : null;
      var m = mode();
      writeUrl(!!state.lastMode && state.lastMode !== m && !state.restoring);
      state.lastMode = m; state.restoring = false;
      renderChips();
      var sel = selectedChems();
      refs.shell.classList.toggle('is-idle', m === 'idle');
      refs.shell.classList.toggle('is-browse', m === 'browse');
      if (m === 'idle') {
        if (state.drawer) toggleDrawer(false);  // otherwise the page stays scroll-locked behind an empty backdrop
        refs.side.innerHTML = ''; refs.toolbar.innerHTML = ''; renderIntro(); announce(''); return;
      }
      var ev = m === 'browse' ? { results: browseItems, upgrades: [], askUs: [], listed: [] } : engine.evaluate(products, sel, data, state.flam, showListed);
      var facets = engine.facets(ev.results.concat(ev.upgrades, ev.listed), opts, state.filters);  // the filters apply to every list
      var filtered = engine.applyFilters(ev.results, state.filters, opts), upgrades = engine.applyFilters(ev.upgrades, state.filters, opts), listed = engine.applyFilters(ev.listed, state.filters, opts);
      renderSide(facets, filtered.length + upgrades.length + listed.length);
      renderToolbar(filtered.length, ev.results.length, m, upgrades.length, ev.upgrades.length, listed.length, ev.listed.length);
      renderResults(filtered, upgrades, ev.askUs, sel, m, listed);
      if (focusId) { var again = document.getElementById(focusId); if (again) again.focus(); }
      var noun = function (n) { return n + (m === 'browse' ? ' pump' : ' rated pump') + (n === 1 ? '' : 's'); };
      announce((filtered.length === ev.results.length ? noun(filtered.length) : filtered.length + ' of ' + noun(ev.results.length) + ' shown') + (upgrades.length ? ', ' + upgrades.length + ' with a required upgrade' : '') + (listed.length ? ', ' + listed.length + ' ' + listedCount() : ''));
      var sig = location.search;
      if (sig !== state.lastSig) {
        state.lastSig = sig;
        if (m === 'browse') track('sbc_browse', { sbc_filters: filterSummary(), sbc_count: filtered.length });
        else track('sbc_results', { sbc_chemicals: sel.map(function (c) { return c.n; }).join(' + '), sbc_count: filtered.length, sbc_upgrades: ev.upgrades.length, sbc_check_with_us: ev.askUs.length, sbc_listed: ev.listed.length });
      }
    }
    function filterSummary() {
      var f = state.filters, parts = f.type.concat(f.container, f.power, f.compliance.map(function (c) { return c === 'grounded' ? groundCopy('included').short : c; }));
      if (f.priceMax != null) parts.push('under ' + money(f.priceMax));
      return parts.join(', ');
    }
    function startBrowse(preset) {
      state.browse = true; state.chems = []; state.flam = {}; state.filters = emptyFilters(); state.shown = 0; state.shownListed = 0;
      if (preset) state.filters[preset[0]] = [preset[1]];
      render();
      if (refs.sheet && refs.sheet.getBoundingClientRect().top < 0) refs.sheet.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    function fmpLink(placement, cls, children) {
      var copy = rules.find_my_pump || {}, src = copy['src_' + placement] || ('sbc-' + placement);
      var href = fmpUrl + (fmpUrl.indexOf('?') >= 0 ? '&' : '?') + 'src=' + encodeURIComponent(src);
      return el('a', { class: cls, href: href, onclick: function () { track('sbc_fmp_click', { sbc_placement: placement }); } }, children);
    }
    function focusSearch() {
      refs.input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      refs.input.focus({ preventScroll: true });
    }
    // ?q=… from the homepage hero: the text lands in the search box with its suggestions open (the URL is already clean)
    function prefill(q) {
      state.query = '';
      refs.input.value = q;
      refs.input.dispatchEvent(new Event('input'));
      focusSearch();
    }

    function renderChips() {
      refs.chips.innerHTML = '';
      selectedChems().forEach(function (c) {
        var flam = PE.flammable(c, state.flam[c.id]), unknown = PE.flammabilityUnknown(c), ground = flam || !!c.gr;
        // manufacturer notes shown with the chemical, most serious first; conditions appear with the pumps they apply to
        var advice = PE.notes(c, null, ['do-not-use']).concat(PE.notes(c, null, ['warning']), PE.notes(c, null, ['recommendation', 'info']),
          PE.notes(c, null, ['condition']).filter(function (n) { return arr(n.exceptKeys).length; }));
        var chip = el('div', { class: 'sbc-chem' + (flam ? ' is-flammable' : ''), tabindex: '-1' }, [
          el('div', { class: 'sbc-chem-head' }, [
            el('strong', {}, [c.n]),
            c.cas ? el('span', { class: 'sbc-chem-cas' }, ['CAS ' + c.cas]) : null,
            flam ? flag(unknown ? 'Treated as flammable' : (c.f === 'combustible' ? 'Combustible' : 'Flammable'), unknown ? 'unknown' : null) : null,
            el('button', { type: 'button', class: 'sbc-chem-remove', 'aria-label': 'Remove ' + c.n, onclick: function () { removeChemical(c.id); } }, ['×'])
          ]),
          el('div', { class: 'sbc-chem-notes' }, [
            ground ? el('span', { class: 'sbc-note' }, [icon('ground'), (((rules.compliance || {}).grounding || {}).needs_note) || 'Pumps that cannot be grounded are left out. Ground the pump and both containers before use.']) : null,
            arr(c.h).length ? el('span', { class: 'sbc-note' }, [icon('droplet'), el('span', {}, hoseAdvice(c.h))]) : null
          ]),
          advice.length ? el('ul', { class: 'sbc-mnotes' }, advice.map(function (n) {
            var serious = n.k === 'do-not-use' || n.k === 'warning';
            return el('li', { class: serious ? 'is-warn' : null }, [icon(serious ? 'alert' : 'info'), el('span', {}, [el('strong', {}, [PE.sourceLabels(data, [n.s])[0] + (n.k === 'warning' ? ' warning: ' : ': ')]), n.t])]);
          })) : null,
          unknown ? el('div', { class: 'sbc-flamq' }, [
            el('span', {}, ['Is it flammable (flash point under 140 °F)?']),
            flamBtn(c, 'yes', 'Yes'), flamBtn(c, 'no', 'No'), flamBtn(c, 'unsure', 'Not sure')
          ]) : null
        ]);
        refs.chips.appendChild(chip);
      });
      var sel = selectedChems();
      syncSearchPlaceholder();
      if (!sel.length) return;
      if (state.adding && !state.addFocus && !state.addQuery) state.adding = false;  // re-rendered while open but untouched: back to the tile
      if (!isFull()) refs.chips.appendChild(state.adding ? addField() : addTile());
      if (sel.length > 1) refs.chips.appendChild(el('p', { class: 'sbc-blend-note' }, [
        icon('alert'), 'Ratings cover each liquid on its own. Pumping them mixed together? ', el('a', { href: contactLink(sel, true) }, ['Contact us']), '.'
      ]));
    }
    // "+ Add another liquid": the tile turns into a search field in place, so the button is the input
    function addTile() {
      return el('button', { type: 'button', class: 'sbc-chem-add', onclick: function () { state.adding = true; state.addFocus = true; state.addQuery = ''; renderChips(); } }, [
        el('span', { class: 'sbc-chem-add-label' }, [icon('plus'), el('span', {}, ['Add another liquid'])]),
        el('span', { class: 'sbc-chem-add-hint' }, ['Using one pump for several liquids? Add up to ' + rules.max_chemicals + '.'])
      ]);
    }
    function addField() {
      function stop(refocus) {
        state.adding = false; state.addQuery = ''; renderChips();
        if (refocus) { var t = refs.chips.querySelector('.sbc-chem-add'); if (t) t.focus(); }
      }
      var cb = combobox({ suffix: 'add', placeholder: 'Add another liquid', label: 'Add another liquid', onEscape: function () { stop(true); },
        onBlur: function (inp) { if (inp.isConnected && state.adding && !inp.value.trim()) stop(false); } });  // a field replaced by a re-render is ignored
      cb.input.value = state.addQuery || '';
      cb.input.addEventListener('input', function () { state.addQuery = cb.input.value; });
      if (state.addFocus) { state.addFocus = false; setTimeout(function () { cb.input.focus(); }, 0); }
      return el('div', { class: 'sbc-chem-add is-open' }, [el('div', { class: 'sbc-searchwrap' }, [icon('search', 'sbc-searchicon'), cb.input, cb.list])]);
    }
    // "Use a Tygon or PTFE hose." with each hose name linking to its product
    function hoseAdvice(hoses) {
      var shop = (rules.hose && rules.hose.shop) || {}, parts = ['Use a '];
      hoses.forEach(function (h, i) {
        if (i) parts.push(i === hoses.length - 1 ? ' or ' : ', ');
        parts.push(shop[h] ? el('a', { class: 'sbc-hose-link', href: shopRoot() + shop[h], onclick: function () { track('sbc_hose_click', { sbc_hose: h }); } }, [h]) : h);
      });
      parts.push(' hose.');
      return parts;
    }
    function flamBtn(c, v, label) {
      var cur = state.flam[c.id] || 'unsure';
      return el('button', { type: 'button', class: 'sbc-pillbtn' + (cur === v ? ' is-on' : ''), 'aria-pressed': String(cur === v), 'data-flam': c.id + ':' + v, onclick: function () {
        state.flam[c.id] = v; state.shown = 0; state.shownListed = 0; render();
        var again = Array.prototype.filter.call(refs.chips.querySelectorAll('[data-flam]'), function (b) { return b.getAttribute('data-flam') === c.id + ':' + v; })[0];
        if (again) again.focus();  // the boxes are rebuilt; keep focus on the answer just given
      } }, [label]);
    }
    // Remove a liquid and keep keyboard focus nearby: the box now in its place, the last box, or the search field
    function removeChemical(id) {
      var index = state.chems.indexOf(id);
      state.chems = state.chems.filter(function (x) { return x !== id; }); delete state.flam[id]; state.shown = 0; state.shownListed = 0;
      render();
      var chips = refs.chips.querySelectorAll('.sbc-chem');
      if (chips.length) chips[Math.min(index, chips.length - 1)].focus(); else refs.input.focus();
    }

    function renderIntro() {
      refs.main.innerHTML = '';
      var popular = arr(rules.common_searches && rules.common_searches.items);
      var found = popular.map(function (p) { var c = PE.byId(chems, p[0]); return c && { c: c, label: p[1] }; }).filter(Boolean);
      if (found.length) refs.main.appendChild(el('div', { class: 'sbc-intro' }, [
        el('h2', {}, ['Common searches']),
        el('div', { class: 'sbc-popular' }, found.map(function (f) { return el('button', { type: 'button', class: 'sbc-pillbtn', title: f.c.n, onclick: function () { state.chems = [f.c.id]; state.flam = {}; state.shown = 0; state.shownListed = 0; track('sbc_search', { sbc_chemical: f.c.n, sbc_position: 1, sbc_common: true }); render(); } }, [f.label]); }))
      ]));
      var copy = rules.find_my_pump || {};
      var fmpCard = fmpUrl ? el('div', { class: 'sbc-fmp-card' }, [
        icon('compass', 'sbc-fmp-icon'),
        el('div', { class: 'sbc-fmp-copy' }, [el('h3', {}, [copy.landing_heading || 'Not sure what you need?']), el('p', {}, [copy.landing_text || 'Answer a few questions about your liquid and container. Find My Pump picks one pump for you.'])]),
        fmpLink('landing', 'sbc-fmp-btn', [el('span', {}, [copy.landing_button || 'Try Find My Pump']), icon('arrow')])
      ]) : null;
      if (!allowBrowse || !browseItems.length) {
        if (fmpCard) { refs.main.appendChild(el('div', { class: 'sbc-or', role: 'separator' }, [el('span', {}, ['or'])])); refs.main.appendChild(fmpCard); }
        return;
      }
      var counts = engine.facets(browseItems, opts);
      var quick = arr(rules.browse && rules.browse.quick).filter(function (q) { return (counts[q[0]] || {})[q[1]]; });
      refs.main.appendChild(el('div', { class: 'sbc-or', role: 'separator' }, [el('span', {}, ['or'])]));
      refs.main.appendChild(el('div', { class: 'sbc-intro sbc-browse-intro' }, [
        el('h2', {}, [(rules.browse && rules.browse.heading) || 'Shop by container']),
        quick.length ? el('div', { class: 'sbc-popular' }, quick.map(function (q) {
          return el('button', { type: 'button', class: 'sbc-pillbtn sbc-quick', onclick: function () { startBrowse(q); } }, [el('span', {}, [q[1]]), el('span', { class: 'sbc-quick-count' }, [String(counts[q[0]][q[1]])])]);
        })) : null,
        el('button', { type: 'button', class: 'button-primary sbc-browse-all', onclick: function () { startBrowse(); } }, [root.getAttribute('data-browse-label') || ('See all ' + browseItems.length + ' pumps')])
      ]));
      if (fmpCard) refs.main.appendChild(fmpCard);
    }

    // Wording for the three grounding states comes from the rules file, so it can be changed without a code change
    function groundCopy(state) { return ((rules.compliance || {}).grounding || {})[state] || { short: state, label: state, note: '' }; }
    function complianceLabel(value) {
      if (value === 'grounded') return groundCopy('included').label;
      return value;
    }
    function renderSide(facets, shown) {
      refs.side.innerHTML = '';
      var head = el('div', { class: 'sbc-side-head' }, [el('h2', {}, ['Filters']), el('button', { type: 'button', class: 'sbc-close', 'aria-label': 'Close filters', onclick: function () { toggleDrawer(false); } }, ['×'])]);
      refs.side.appendChild(head);
      var LEGEND_ICON = { type: 'tool', container: 'drum', power: 'bolt', compliance: 'shield' };
      function group(title, key, options) {
        var g = el('fieldset', { class: 'sbc-fgroup' }, [el('legend', {}, [LEGEND_ICON[key] ? icon(LEGEND_ICON[key]) : null, el('span', {}, [title])])]);
        options.forEach(function (o) {
          var label = o[0], count = o[1], value = o[2] || o[0], on = state.filters[key].indexOf(value) >= 0;
          var cb = el('input', { type: 'checkbox', id: uid + '-' + key + '-' + value.replace(/\W+/g, '-'), disabled: (!count && !on) ? '' : null, onchange: function () {
            var f = state.filters[key]; if (cb.checked) f.push(value); else state.filters[key] = f.filter(function (x) { return x !== value; }); state.shown = 0; state.shownListed = 0; render();
          } });
          cb.checked = on;
          g.appendChild(el('label', { class: 'sbc-check' + ((!count && !on) ? ' is-empty' : ''), for: cb.id }, [cb, el('span', {}, [label]), el('span', { class: 'sbc-count' }, [String(count || 0)])]));
        });
        return g.querySelector('label') ? g : null;
      }
      var types = Object.keys(facets.type).sort(function (a, b) { return facets.type[b] - facets.type[a]; }).map(function (t) { return [t, facets.type[t]]; });
      state.filters.type.forEach(function (t) { if (!facets.type[t]) types.push([t, 0]); });  // ticked but now empty: keep it so it can be unticked
      [group('Pump type', 'type', types),
       // options the rules list are always shown (greyed when empty); values added from the catalog only when they have pumps
       group(rules.filters.container.label, 'container', opts.container.map(function (o, i) { return [o[0], facets.container[o[0]] || 0, null, i >= rules.filters.container.options.length]; }).filter(function (o) { return !o[3] || o[1] || state.filters.container.indexOf(o[0]) >= 0; })),
       group(rules.filters.power.label, 'power', opts.power.map(function (o, i) { return [o[0], facets.power[o[0]] || 0, null, i >= rules.filters.power.options.length]; }).filter(function (o) { return !o[3] || o[1] || state.filters.power.indexOf(o[0]) >= 0; })),
       group('Safety & compliance', 'compliance', opts.compliance.map(function (v) { return [complianceLabel(v), facets.compliance[v] || 0, v]; }))
      ].forEach(function (g) { if (g) refs.side.appendChild(g); });
      // price
      var pg = el('fieldset', { class: 'sbc-fgroup' }, [el('legend', {}, [icon('tag'), el('span', {}, ['Max price'])])]);
      var max = Math.ceil(facets.priceMax / 50) * 50 || 50, cur = state.filters.priceMax != null ? Math.min(state.filters.priceMax, max) : max;
      var out = el('output', { class: 'sbc-price-out' }, [cur >= max ? 'Any' : money(cur)]);
      var range = el('input', { id: uid + '-price', type: 'range', min: String(Math.floor(facets.priceMin / 10) * 10), max: String(max), step: '10', value: String(cur), 'aria-label': 'Maximum price', oninput: function () { out.textContent = Number(range.value) >= max ? 'Any' : money(range.value); }, onchange: function () { state.filters.priceMax = Number(range.value) >= max ? null : Number(range.value); state.shown = 0; state.shownListed = 0; render(); } });
      pg.appendChild(el('div', { class: 'sbc-price' }, [range, out]));
      refs.side.appendChild(pg);
      var active = activeFilterCount();
      if (active) refs.side.appendChild(el('button', { type: 'button', class: 'sbc-link', onclick: function () { state.filters = emptyFilters(); state.shown = 0; state.shownListed = 0; render(); } }, ['Clear filters (' + active + ')']));
      refs.side.appendChild(el('button', { type: 'button', class: 'button-primary sbc-apply', onclick: function () { toggleDrawer(false); } }, ['Show ' + shown + ' pump' + (shown === 1 ? '' : 's')]));
      syncDrawerA11y();
    }
    function activeFilterCount() { var f = state.filters; return f.type.length + f.container.length + f.power.length + f.compliance.length + (f.priceMax != null ? 1 : 0); }
    function toggleDrawer(open) {
      state.drawer = open;
      refs.side.classList.toggle('is-open', open); refs.backdrop.classList.toggle('is-open', open); document.body.classList.toggle('sbc-lock', open);
      syncDrawerA11y();
      if (refs.filterBtn) refs.filterBtn.setAttribute('aria-expanded', String(!!open));
      if (open) { var first = refs.side.querySelector('input, button'); if (first) first.focus(); }
      else if (refs.filterBtn && document.activeElement && refs.side.contains(document.activeElement)) refs.filterBtn.focus();
    }
    function stickyOffset() {
      var cands = document.querySelectorAll('header, .site-header, .site-header-wrapper, [data-site-header], .sticky-header');
      var h = 0;
      Array.prototype.forEach.call(cands, function (e) {
        var cs = getComputedStyle(e), r = e.getBoundingClientRect();
        if ((cs.position === 'sticky' || cs.position === 'fixed') && r.bottom > 0 && r.height > h) h = r.bottom;
      });
      root.style.setProperty('--sbc-sticky-top', Math.round(h + 16) + 'px');
    }
    function watchStickyHeader() {
      var queued = false;
      function schedule() { if (queued) return; queued = true; requestAnimationFrame(function () { queued = false; stickyOffset(); }); }
      on(window, 'scroll', schedule, { passive: true });
      on(window, 'resize', schedule);
      stickyOffset();
    }
    function isMobile() { return window.matchMedia('(max-width: 900px)').matches; }
    function syncDrawerA11y() {
      var mobile = isMobile(), closed = mobile && !state.drawer;
      if (mobile) { refs.side.setAttribute('role', 'dialog'); refs.side.setAttribute('aria-modal', String(!!state.drawer)); } else { refs.side.removeAttribute('role'); refs.side.removeAttribute('aria-modal'); }
      if (closed) { refs.side.setAttribute('inert', ''); refs.side.setAttribute('aria-hidden', 'true'); } else { refs.side.removeAttribute('inert'); refs.side.removeAttribute('aria-hidden'); }
    }
    // Escape closes the drawer; Tab and Shift+Tab stay inside it while it is open (it is announced as a modal dialog)
    on(document, 'keydown', function (e) {
      if (!state.drawer) return;
      if (e.key === 'Escape') { toggleDrawer(false); return; }
      if (e.key !== 'Tab') return;
      var f = Array.prototype.filter.call(refs.side.querySelectorAll('button, input, select, a[href], [tabindex]:not([tabindex="-1"])'), function (x) { return !x.disabled && x.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (!refs.side.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    on(window, 'popstate', function () {
      if (!refs.shell || location.search === state.lastSearch) return;
      if (state.drawer) toggleDrawer(false);
      state.chems = []; state.flam = {}; state.filters = emptyFilters(); state.browse = false; state.adding = false; state.addQuery = ''; state.shown = 0; state.shownListed = 0; state.restoring = true;
      readUrl(); render();
    });
    on(window, 'resize', function () { if (!refs.side) return; if (state.drawer && !isMobile()) toggleDrawer(false); else syncDrawerA11y(); });

    function listedCopy() { return rules.listed || {}; }
    function listedCount() { var c = listedCopy(); return (selectedChems().length > 1 ? c.count_several : c.count) || c.count || 'designed for it'; }
    function renderToolbar(shown, total, m, upShown, upTotal, liShown, liTotal) {
      refs.toolbar.innerHTML = '';
      var n = activeFilterCount();
      var line = m === 'browse'
        ? (shown === total ? 'All ' + total + ' pumps' : shown + ' of ' + total + ' pumps')
        : (shown === total ? (total + ' rated pump' + (total === 1 ? '' : 's')) : (shown + ' of ' + total + ' rated pumps'));
      if (upTotal) line += ' · ' + (upShown === upTotal ? upTotal : upShown + ' of ' + upTotal) + ' with a required upgrade';
      if (liTotal) line += ' · ' + (liShown === liTotal ? liTotal : liShown + ' of ' + liTotal) + ' ' + listedCount();
      refs.toolbar.appendChild(el('p', { class: 'sbc-count-line' }, [line]));
      refs.filterBtn = el('button', { type: 'button', class: 'sbc-filterbtn', 'aria-controls': uid + '-side', 'aria-expanded': String(!!state.drawer), onclick: function () { toggleDrawer(true); } }, ['Filters' + (n ? ' (' + n + ')' : '')]);
      refs.toolbar.appendChild(refs.filterBtn);
    }

    // Adds several variants in one request (a pump and its upgrade), then opens the cart
    // "Add both to cart": the pump and its upgrade parts together, then the cart
    function addTogether(p, parts, btn, event) {
      var foot = btn.parentNode, old = foot.parentNode.querySelector('.sbc-cart-error');
      if (old) old.remove();
      btn.disabled = true; var label = btn.textContent; btn.textContent = 'Adding…';
      PE.addTogether(shopRoot(), p.variant_id, parts.map(function (part) { return part.product.variant_id; })).then(function (res) {
        var message = PE.cartMessage(res), cart = cartUrl || (shopRoot() + 'cart');
        if (res.ok) track(event.name, event.data);
        if (res.ok && !message) { window.location.href = cart; return; }
        var note = el('p', { class: 'sbc-cart-error', role: 'alert' }, [p.title + ': ' + message, ' ', el('a', { href: cart }, ['View cart'])]);
        if (btn.isConnected) {
          btn.disabled = res.ok; btn.textContent = res.ok ? 'Added' : label;
          note.firstChild.textContent = message;
          foot.insertAdjacentElement('afterend', note);
        } else if (refs.main) {
          refs.main.insertBefore(note, refs.main.firstChild);  // the results were redrawn while adding: show it above them
        }
      });
    }
    function card(it) {
      var p = it.p, c = it.compliance, rated = !!it.rating, parts = arr(it.parts), several = selectedChems().length > 1, named = arr(it.listed).length > 0;
      // the chemical's name and hose advice are already in its box above the results, so cards only name the chart
      var srcText = rated ? 'Rated by ' + listText(PE.sourceLabels(data, it.sources)) : null;
      var certs = (rules.compliance && rules.compliance.certifications) || {};
      var badges = [
        rated ? el('span', { class: 'sbc-rating' + (it.rank <= 1 ? ' is-low' : it.rank === 2 ? ' is-mid' : '') }, [icon('check'), el('span', {}, [it.rating])]) : null,
        named ? el('span', { class: 'sbc-badge sbc-named' }, [icon('info'), el('span', {}, [listedCopy().badge || 'Not chart-rated'])]) : null,
        (function () {  // every pump says how it is grounded, so nobody has to guess
          var g = groundCopy(c.grounding);
          return el('span', { class: 'sbc-badge sbc-ground-' + c.grounding, title: g.note || '' }, [icon('ground'), el('span', {}, [g.short])]);
        })()
      ].concat(c.certs.map(function (v) {
        var cfg = certs[v] || {};
        return el('span', { class: 'sbc-badge', title: cfg.note || v }, [icon(cfg.icon || 'shield'), el('span', {}, [cfg.short || v])]);
      })).filter(Boolean);
      return el('article', { class: 'sbc-card' }, [
        el('a', { class: 'sbc-card-media', href: p.url, 'aria-hidden': 'true', tabindex: '-1' }, [p.image ? el('img', { src: p.image, alt: '', loading: 'lazy' }) : el('span', { class: 'sbc-noimg' })]),
        el('div', { class: 'sbc-card-body' }, [
          badges.length ? el('div', { class: 'sbc-badges' }, badges) : null,
          el('h3', { class: 'sbc-card-title' }, [el('a', { href: p.url }, [p.title])]),
          el('p', { class: 'sbc-card-meta' }, [[it.type, p.power, p.flow].filter(Boolean).join(' · ')]),
          rated ? el('p', { class: 'sbc-card-src' }, [srcText]) : null,
          named ? el('p', { class: 'sbc-card-src' }, [engine.fill(listedCopy().card || 'The manufacturer\u2019s page says it\u2019s designed for {named}', { named: engine.namedText([it]) })]) : null,
          // for a flammable liquid, say on the pump itself that the customer has to ground this one
          c.grounding === 'customer' && selectedChems().some(function (ch) { return PE.needsGroundable(ch, state.flam[ch.id]); })
            ? el('p', { class: 'sbc-card-ground' }, [icon('ground'), el('span', {}, [groundCopy('customer').note])]) : null,
          arr(it.included).length ? el('p', { class: 'sbc-card-included' }, [icon('check'), 'Built with the ' + listText(arr(it.included).filter(function (id) { return !arr(it.included).some(function (o) { return o !== id && arr((data.parts || {})[o] && data.parts[o].covers).indexOf(id) >= 0; }); }).map(function (id) { return ((data.parts || {})[id] || {}).label || id; }))]) : null,
          engine.noteLines(it.warnings, data, several).length ? el('ul', { class: 'sbc-card-warn' }, engine.noteLines(it.warnings, data, several).map(function (line) { return el('li', {}, [icon('alert'), el('span', {}, [line])]); })) : null,
          parts.length ? el('div', { class: 'sbc-upgrade' }, [
            el('p', { class: 'sbc-upgrade-head' }, ['Required upgrade']),
            el('ul', {}, parts.map(function (part) {
              return el('li', {}, [el('a', { href: part.product.url }, [part.label]), ' · ' + money(part.product.price), part.note ? el('span', { class: 'sbc-upgrade-note' }, [part.note]) : null]);
            })),
            el('p', { class: 'sbc-upgrade-why' }, [engine.noteLines(it.reasons, data, several).join(' ')])
          ]) : null,
          el('div', { class: 'sbc-card-foot' }, [
            el('span', { class: 'sbc-price-tag' }, [money(it.total), parts.length ? el('small', {}, [' with upgrade']) : null]),
            parts.length && p.variant_id
              ? el('button', { type: 'button', class: 'button-primary sbc-add', onclick: function (e) { addTogether(p, parts, e.currentTarget, { name: 'sbc_add_upgrade_to_cart', data: { sbc_product: p.handle, sbc_upgrade: parts.map(function (part) { return part.id; }).join('+') } }); } }, [parts.length === 1 ? 'Add both to cart' : 'Add all to cart'])
              : p.variant_id ? el('form', { method: 'post', action: shopRoot() + 'cart/add', class: 'sbc-cart', onsubmit: function () { track('sbc_add_to_cart', { sbc_product: p.handle }); } }, [el('input', { type: 'hidden', name: 'id', value: p.variant_id }), el('input', { type: 'hidden', name: 'quantity', value: '1' }), el('input', { type: 'hidden', name: 'return_to', value: location.pathname + location.search }), el('button', { type: 'submit', class: 'button-primary sbc-add' }, ['Add to cart'])]) : el('a', { class: 'button-primary sbc-add', href: p.url }, ['View'])
          ])
        ])
      ]);
    }

    function contactFor(query) {
      var sel = selectedChems(), summary = 'No rating found for "' + query + '"' + (sel.length ? ', together with ' + sel.map(function (c) { return c.n; }).join(' + ') : '');  // the contact snippet adds the tool name
      return contactUrl + (contactUrl.indexOf('?') >= 0 ? '&' : '?') + 'fmp=' + encodeURIComponent(summary) + '&src=shop-by-chemical';
    }
    function contactLink(sel, blend) {
      // the contact snippet adds the tool name ("Shop by Chemical request: …"), so the summary starts with the content
      var summary = sel.length ? (blend ? 'Blend of ' : '') + sel.map(function (c) { return c.n + (c.cas ? ' (CAS ' + c.cas + ')' : ''); }).join(' + ')
                               : 'Browsing all pumps' + (filterSummary() ? ' (' + filterSummary() + ')' : '') + ', chemical not specified';
      return contactUrl + (contactUrl.indexOf('?') >= 0 ? '&' : '?') + 'fmp=' + encodeURIComponent(summary) + '&src=shop-by-chemical';
    }

    // open: shown expanded when no pump is rated, so the conditions the empty state points to are visible
    function renderAskUs(askUs, sel, open) {
      var det = el('details', { class: 'sbc-askus', open: open ? '' : null }, [el('summary', {}, [icon('help'), askUs.length + ' more pump' + (askUs.length === 1 ? '' : 's') + ' to check with us'])]);
      det.appendChild(el('p', { class: 'sbc-muted' }, ['The manufacturer’s rating for these comes with a condition. ', el('a', { href: contactLink(sel) }, ['Check with us']), ' before you buy and we’ll confirm the right setup.']));
      var shownItems = 0;
      engine.askGroups(askUs, data, sel.length > 1).forEach(function (g) {
        var links = engine.partLinks(g.texts, rules, g.items, data);
        var box = el('div', { class: 'sbc-askgroup' }, [
          el('ul', { class: 'sbc-reasons' }, g.lines.map(function (line) { return el('li', {}, [icon('alert'), el('span', {}, [line])]); })),
          links.length ? el('p', { class: 'sbc-parts' }, ['Parts: '].concat(links.reduce(function (acc, l, i) {
            if (i) acc.push(', ');
            acc.push(el('a', { href: shopRoot() + l.url, onclick: function () { track('sbc_part_click', { sbc_part: l.label }); } }, [l.label]));
            return acc;
          }, []))) : null
        ]);
        var rest = Math.max(0, 30 - shownItems), list = g.items.slice(0, rest);
        shownItems += list.length;
        if (list.length) box.appendChild(el('ul', { class: 'sbc-asklist' }, list.map(function (it) { return el('li', {}, [el('a', { href: it.p.url }, [it.p.title]), ' · ' + money(it.p.price)]); })));
        if (g.items.length > list.length) box.appendChild(el('p', { class: 'sbc-muted' }, [(g.items.length - list.length) + ' more. ', el('a', { href: contactLink(sel) }, ['Ask us about them']), '.']));
        det.appendChild(box);
      });
      return det;
    }

    function renderUpgrades(upgrades, sel) {
      return el('section', { class: 'sbc-upgrades', 'aria-label': 'Rated with a required upgrade' }, [
        el('h2', {}, ['Rated with a required upgrade (' + upgrades.length + ')']),
        el('p', { class: 'sbc-muted' }, ['The manufacturer rates these pumps for ' + (sel.length > 1 ? 'these liquids' : 'this liquid') + ' once the upgrade shown is added. Order the pump and the upgrade together.']),
        el('div', { class: 'sbc-cards' }, upgrades.map(card))
      ]);
    }

    function renderListed(listed, sel) {
      var copy = listedCopy(), size = rules.results_page_size, end = Math.min(listed.length, Math.max(size, state.shownListed || 0));
      var liquid = sel.map(function (c) { return c.n; }).join(' + ');
      var box = el('section', { class: 'sbc-upgrades sbc-listed', 'aria-label': copy.heading || 'Designed for this liquid, not chart-rated' }, [
        el('h2', {}, [(copy.heading || 'Designed for this liquid, not chart-rated') + ' (' + listed.length + ')']),
        el('p', { class: 'sbc-muted' }, [sel.length > 1 ? (copy.note_several || '') : engine.fill(copy.note || '', { liquid: liquid, named: engine.namedText(listed) })]),
        el('div', { class: 'sbc-cards' }, listed.slice(0, end).map(card))
      ]);
      if (end < listed.length) box.appendChild(el('button', { type: 'button', class: 'sbc-more', onclick: function () {
        state.shownListed = end + size; render();
        var cards = refs.main.querySelectorAll('.sbc-listed .sbc-card'), firstNew = cards[end];
        if (firstNew) { firstNew.scrollIntoView({ block: 'start', behavior: 'auto' }); var a = firstNew.querySelector('.sbc-card-title a'); if (a) a.focus({ preventScroll: true }); }
      } }, ['Show more (' + (listed.length - end) + ' left)']));
      state.shownListed = end;
      return box;
    }

    function renderResults(items, upgrades, askUs, sel, m, listed) {
      listed = arr(listed);
      refs.main.innerHTML = '';
      var browsing = m === 'browse';
      if (browsing) refs.main.appendChild(el('div', { class: 'sbc-browse-note', role: 'note' }, [
        icon('alert'),
        el('p', {}, ['These pumps aren’t matched to a chemical.']),
        el('div', { class: 'sbc-browse-note-links' }, [
          el('button', { type: 'button', class: 'sbc-note-link', onclick: focusSearch }, [el('span', {}, ['Add your chemical']), icon('arrowup')]),
          fmpUrl ? fmpLink('browse', 'sbc-note-link', [el('span', {}, [(rules.find_my_pump || {}).browse_text || 'Let Find My Pump pick one']), icon('arrow')]) : null
        ])
      ]));
      var filtered = activeFilterCount() > 0;
      if (!items.length && upgrades.length && !filtered) {
        refs.main.appendChild(el('div', { class: 'sbc-upnotice', role: 'note' }, [icon('tool'), el('p', {}, ['No pump is rated for ' + (sel.length > 1 ? 'these liquids' : 'this liquid') + ' as sold. The pumps below are rated once the required upgrade is added.'])]));
      } else if (!items.length && listed.length && !filtered) {
        refs.main.appendChild(el('div', { class: 'sbc-upnotice', role: 'note' }, [icon('info'), el('p', {}, [engine.fill(listedCopy().notice || '', { liquid: sel.map(function (c) { return c.n; }).join(' + ') })])]));
      } else if (!items.length) {
        if (!browsing && location.search !== state.lastNoMatch) { state.lastNoMatch = location.search; track('sbc_no_match', { sbc_chemicals: sel.map(function (c) { return c.n; }).join(' + '), sbc_upgrades: upgrades.length, sbc_check_with_us: askUs.length }); }
        var conditional = !filtered && askUs.length > 0;
        refs.main.appendChild(el('div', { class: 'sbc-empty' }, [
          icon('nomatch', 'sbc-empty-icon'),
          el('h2', {}, [filtered ? (browsing ? 'No pumps match these filters' : 'No rated pumps match these filters') : conditional ? 'No pump is rated without a condition' : 'No rated pump for this chemical']),
          el('p', {}, [filtered ? 'Remove a filter to see more.' : conditional ? 'The pumps below need a part, a limit or our advice first. Tell us about your setup and we’ll recommend the right one.' : 'No manufacturer rates a pump we sell for it, and we won’t guess. Tell us what you’re pumping and we’ll recommend one.']),
          el('div', { class: 'sbc-empty-actions' }, filtered
            ? [el('button', { type: 'button', class: 'button-primary', onclick: function () { state.filters = emptyFilters(); state.shown = 0; state.shownListed = 0; render(); } }, ['Clear filters']), el('a', { class: 'sbc-link', href: contactLink(sel) }, ['or contact us'])]
            : [el('a', { class: 'button-primary', href: contactLink(sel) }, ['Contact us'])])
        ]));
      } else {
        var size = rules.results_page_size, end = Math.min(items.length, Math.max(size, state.shown || 0));
        var grid = el('div', { class: 'sbc-cards' }, items.slice(0, end).map(card));
        refs.main.appendChild(grid);
        if (end < items.length) refs.main.appendChild(el('button', { type: 'button', class: 'sbc-more', onclick: function () {
          state.shown = end + size; render();
          var firstNew = refs.main.querySelectorAll('.sbc-card')[end]; if (firstNew) { firstNew.scrollIntoView({ block: 'start', behavior: 'auto' }); var a = firstNew.querySelector('.sbc-card-title a'); if (a) a.focus({ preventScroll: true }); }
        } }, ['Show more (' + (items.length - end) + ' left)']));
        state.shown = end;
      }
      if (upgrades.length) refs.main.appendChild(renderUpgrades(upgrades, sel));
      if (listed.length) refs.main.appendChild(renderListed(listed, sel));
      if (askUs.length) refs.main.appendChild(renderAskUs(askUs, sel, !items.length && !upgrades.length && !filtered));
      if (browsing) { refs.main.appendChild(el('p', { class: 'sbc-foot' }, ['Need help choosing? ', el('a', { href: contactLink(sel) }, ['Contact us']), '.'])); return; }
      var used = Object.keys(data.sources || {}).filter(function (id) { return sel.some(function (c) { return arr(c.src).indexOf(id) >= 0; }); });
      var docs = used.map(function (id) { return data.sources[id].document || data.sources[id].label; });
      var limits = used.map(function (id) { return data.sources[id].note; }).filter(Boolean).join(' ');
      refs.main.appendChild(el('p', { class: 'sbc-foot' }, [(docs.length ? 'Ratings come from ' + listText(docs) + '. ' : '') + (limits ? limits + ' ' : '') + (listed.length && listedCopy().foot ? listedCopy().foot + ' ' : '') + 'Not sure? ', el('a', { href: contactLink(sel) }, ['Contact us']), '.']));
    }
  }

  var SBC = { engine: engine, mount: mount };
  if (inNode) module.exports = SBC;
  else if (!window.SBC) {  // the section's script tag can appear more than once on a page; run once
    window.SBC = SBC;
    var boot = function (scope) { Array.prototype.forEach.call((scope || document).querySelectorAll('[data-sbc]'), mount); };
    document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', function () { boot(); }) : boot();
    // Theme editor: sections are re-rendered in place without a page load
    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
    document.addEventListener('shopify:section:unload', function (e) {
      Array.prototype.forEach.call(e.target.querySelectorAll('[data-sbc]'), function (r) { if (r.__sbc) r.__sbc.destroy(); });
    });
  }
})();
